import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faArrowUpRightFromSquare, faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { TranslocoDirective } from '@jsverse/transloco';
import { BillingStatusService } from '../../../services/billing-status.service';
import { WineryContextService } from '../../../services/winery-context.service';
import { ButtonDirective } from '../../directives/button.directive';
import { HelpPanelService } from '../../help';
import { AccountMenuComponent } from '../account-menu/account-menu.component';
import { BadgeComponent } from '../badge.component';

/** The right side of the top bar: plan chip, booking-page shortcut, help and account menu. */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-top-bar-account',
  standalone: true,
  imports: [RouterLink, FaIconComponent, ButtonDirective, TranslocoDirective, AccountMenuComponent, BadgeComponent],
  host: { class: 'ml-auto flex h-app-header-inner shrink-0 items-center gap-2' },
  template: `
    <ng-container *transloco="let t">
      @if (chip(); as chip) {
        <a routerLink="/settings/plan" class="tap-target -my-3.5 rounded-full lg:my-0" [title]="t(chip.titleKey, chip.params)">
          <app-badge [color]="chip.tone" [label]="t(chip.labelKey, chip.params)" />
        </a>
      }
      @if (winery(); as current) {
        <a
          appButton="ghost"
          size="sm"
          shape="square"
          [href]="current.bookingPageUrl"
          [attr.aria-label]="t('nav.openBookingPage') + ' ' + t('common.opensInNewTab')"
          [title]="t('nav.openBookingPage')"
          target="_blank"
          rel="noopener noreferrer"
        >
          <fa-icon [icon]="externalIcon" aria-hidden="true" />
        </a>
      }
      <button appButton="ghost" size="sm" shape="square" type="button" [attr.aria-label]="t('nav.help')" [title]="t('nav.help')" (click)="openHelp()">
        <fa-icon [icon]="helpIcon" aria-hidden="true" />
      </button>
      <app-account-menu />
    </ng-container>
  `,
})
export class TopBarAccountComponent {
  private readonly _help = inject(HelpPanelService);
  private readonly _billingChip = inject(BillingStatusService).chip;

  protected readonly winery = inject(WineryContextService).winery;
  protected readonly externalIcon = faArrowUpRightFromSquare;
  protected readonly helpIcon = faCircleQuestion;

  /** Plan chip: the short label and the longer title come from the same days-left state, each with its own translation key and params. */
  protected readonly chip = computed(() => {
    const billing = this._billingChip();
    if (!billing) return null;
    const { days, tone } = billing;
    if (days === null) return { tone, titleKey: 'trialNotice.bookingPageClosed', labelKey: 'trialNotice.bookingPageClosed', params: {} };
    const lastDay = days <= 1;
    return {
      tone,
      titleKey: lastDay ? 'trialNotice.lastDay' : 'trialNotice.daysLeft',
      labelKey: lastDay ? 'trialNotice.chipLastDay' : 'trialNotice.chipDaysLeft',
      params: { count: days },
    };
  });

  protected openHelp(): void {
    this._help.open();
  }
}
